'use client'

/**
 * RiskRewardCalculator: free tool for "risk reward calculator / risk reward ratio". Entry, stop and
 * target → reward-to-risk ratio, the breakeven win rate that ratio implies, and (optionally) expectancy
 * per dollar risked at a given win rate. Direction-aware (long or short). Pure client math.
 */

import { useMemo, useState } from 'react'
import CalcField from '@/components/calc/CalcField'

const fmtPct = (n: number): string => (isFinite(n) ? `${n.toFixed(1)}%` : '0.0%')

export default function RiskRewardCalculator() {
  const [entry, setEntry] = useState('10.00')
  const [stop, setStop] = useState('9.50')
  const [target, setTarget] = useState('11.50')
  const [winRate, setWinRate] = useState('')

  const r = useMemo(() => {
    const e = parseFloat(entry) || 0
    const s = parseFloat(stop) || 0
    const t = parseFloat(target) || 0
    const risk = Math.abs(e - s)
    const reward = Math.abs(t - e)
    const ratio = risk > 0 ? reward / risk : 0
    const breakevenWin = reward + risk > 0 ? (risk / (reward + risk)) * 100 : 0
    const wr = parseFloat(winRate)
    const hasWr = isFinite(wr) && wr >= 0 && wr <= 100
    // Expectancy per $1 risked = winRate*ratio - lossRate*1 (loss = 1R).
    const expectancy = hasWr ? (wr / 100) * ratio - (1 - wr / 100) : 0
    const invalid = risk === 0 || e <= 0
    return { risk, reward, ratio, breakevenWin, hasWr, expectancy, invalid }
  }, [entry, stop, target, winRate])

  const good = r.ratio >= 2
  const accent = good ? '#34d399' : r.ratio >= 1 ? '#f59e0b' : '#f43f5e'

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 sm:p-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <CalcField label="Entry price" value={entry} onChange={setEntry} prefix="$" hint="Where you get in" />
        <CalcField label="Stop-loss" value={stop} onChange={setStop} prefix="$" hint="Where you exit if wrong" />
        <CalcField label="Target price" value={target} onChange={setTarget} prefix="$" hint="Where you take profit" />
      </div>
      <div className="mt-4">
        <CalcField label="Your win rate (optional)" value={winRate} onChange={setWinRate} suffix="%" step="1" hint="Add it to see expectancy per $1 risked" />
      </div>

      {r.invalid ? (
        <div className="mt-5 rounded-xl border border-zinc-700/70 bg-zinc-900/60 px-4 py-6 text-center text-sm text-zinc-400">
          Enter an entry, a stop and a target to see your reward-to-risk ratio.
        </div>
      ) : (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border px-5 py-4" style={{ borderColor: `${accent}4d`, background: `${accent}12` }}>
              <div className="text-[12px] font-bold uppercase tracking-wider" style={{ color: accent }}>Reward : Risk</div>
              <div className="mt-1 text-[34px] font-black leading-none tabular-nums text-white">{r.ratio.toFixed(2)} : 1</div>
              <div className="mt-1.5 text-[13px] text-zinc-400">{r.reward.toFixed(2)} reward vs {r.risk.toFixed(2)} risk per share</div>
            </div>
            <div className="rounded-2xl border border-zinc-700/70 bg-zinc-900/60 px-5 py-4">
              <div className="text-[12px] font-bold uppercase tracking-wider text-zinc-400">Breakeven win rate</div>
              <div className="mt-1 text-[34px] font-black leading-none tabular-nums text-white">{fmtPct(r.breakevenWin)}</div>
              <div className="mt-1.5 text-[13px] text-zinc-400">Win more than this and the setup is profitable over time</div>
            </div>
          </div>

          {r.hasWr && (
            <div className="mt-3 rounded-2xl border px-5 py-4" style={{ borderColor: r.expectancy >= 0 ? '#34d39944' : '#f43f5e44', background: r.expectancy >= 0 ? '#34d39912' : '#f43f5e12' }}>
              <span className="text-[13px] font-bold uppercase tracking-wider" style={{ color: r.expectancy >= 0 ? '#34d399' : '#f43f5e' }}>Expectancy</span>
              <div className="mt-1 text-[20px] font-black tabular-nums text-white">
                {r.expectancy >= 0 ? '+' : ''}{r.expectancy.toFixed(2)}R <span className="text-[14px] font-semibold text-zinc-400">per $1 risked</span>
              </div>
              <div className="mt-0.5 text-[12px] text-zinc-500">
                {r.expectancy >= 0
                  ? `At this ratio and win rate, you make about $${r.expectancy.toFixed(2)} on average for every $1 you risk.`
                  : `At this win rate, the ratio is not high enough to be profitable. Raise the target, tighten the stop, or win more often.`}
              </div>
            </div>
          )}
          <p className="mt-3 text-[11px] leading-relaxed text-zinc-600">
            A ratio of 2:1 or better is a common minimum, but the right number depends on your win rate. Educational tool, not investment advice.
          </p>
        </>
      )}
    </div>
  )
}
